import { InjectProperty } from '@jkhy/frontend-utils/di';
import { BuildType, ConfigurationService } from '@treasury/core/config';
import { FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { flagIcon } from '@treasury/presentation/assets/icons/icon-flag';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { showOverrides } from './feature-flag-overrides.helpers';

import './feature-flag-overrides';

const FeatureFlagButtonTagName = 'feature-flag-button';
const BUTTON_SIZE = 50;
const description = 'Feature flag overrides';

@customElement(FeatureFlagButtonTagName)
export class FeatureFlagButton extends LitElement {
    public static styles = css`
        :host {
            /** Keep this above most content but below dialog windows */
            z-index: 999;
        }

        #ff-button {
            height: ${BUTTON_SIZE}px;
            width: ${BUTTON_SIZE}px;
            font-size: 2em;
            text-align: center;
            border: 1px solid;
            border-color: var(--brand-color-dark, #0e5daa);
            border-radius: 25px;
            box-sizing: border-box;
            cursor: pointer;
            padding: 5px;
            color: var(--content-brand, #8ab3e8);
            background-color: var(--brand-color, #0065cf);
            box-shadow: 5px 5px 5px rgba(0, 0, 0, 0.4);
            display: inline-block;
            width: 50px;
            height: 50px;
        }

        #ff-button svg {
            stroke: var(--content-brand, #8ab3e8);
        }
    `;

    /**
     * This component must wait for both DI and
     * the feature service to be ready before rendering.
     */
    @state()
    private ready = false;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly configService: ConfigurationService;

    @InjectProperty({
        async: true,
        token: FeatureFlagService,
    })
    declare private readonly ffServicePromise: Promise<FeatureFlagService>;

    public render() {
        if (!this.ready) {
            return nothing;
        }

        // only allow overrides in development builds
        if (this.configService.buildType === BuildType.Production) {
            return nothing;
        }

        // don't use a component that inherits from TmBaseComponent
        // so it can be used in legacy apps
        return html`<span
            id="ff-button"
            role="img"
            aria-label="${description}"
            @click=${() => this.onClick()}
            >${flagIcon}</span
        >`;
    }

    protected firstUpdated() {
        this.ffServicePromise.then(() => {
            this.ready = true;
        });
    }

    private onClick() {
        showOverrides(this.dialogService);
    }
}
